<template>
  <b-modal id="vulnerabilityCreateVulnerabilityModal" @show="onShow()" @hide="resetValues()" size="lg" hide-header-close :title="$t('message.create_vulnerability')">
    <b-tabs class="body-bg-color" style="border:0;padding:0">
      <b-tab class="body-bg-color" style="border:0;padding:0" active>
        <template v-slot:title><i class="fa fa-edit"></i> {{ $t('message.general') }}</template>
        <b-card>
          <b-input-group-form-input id="vulnerability-id-input" input-group-size="mb-3" type="text" v-model="vulnerability.vulnId"
                                    lazy="true" required="true" feedback="true" autofocus="false"
                                    :label="$t('message.vulnerability_vuln_id')" :tooltip="this.$t('message.vulnerability_vuln_id_desc')"
                                    :feedback-text="$t('message.required_vulnerability_vuln_id')" />
          <b-input-group-form-input id="vulnerability-title-input" input-group-size="mb-3" type="text" v-model="vulnerability.title"
                                    lazy="true" required="false" feedback="false" autofocus="false"
                                    :label="$t('message.vulnerability_title')" :tooltip="this.$t('message.vulnerability_title_desc')"/>
          <b-row>
            <b-col md="6">
              <b-input-group-form-select required="true" v-model="vulnerability.severity" :options="severities"
                                         :label="$t('message.severity')" :tooltip="$t('message.severity')" />
            </b-col>
            <b-col md="6">
              <b-input-group-form-input input-group-size="mb-3" type="text" v-model="computedCvssSeverity"
                                        lazy="true" required="false" feedback="false" autofocus="false" disabled="true"
                                        :label="$t('severity.cvss_severity')" :tooltip="this.$t('message.severity')"/>
            </b-col>
          </b-row>
          <b-form-group :label="this.$t('message.cwe')">
            <div class="list-group">
                        <span v-for="cwe in selectableCwes">
                          <actionable-list-group-item :value="'CWE-'+cwe.cweId" :delete-icon="true" v-on:actionClicked="removeCwe(cwe)"/>
                        </span>
              <actionable-list-group-item :add-icon="true" v-on:actionClicked="$root.$emit('bv::show::modal', 'selectCweModal')"/>
            </div>
          </b-form-group>
          <b-form-group
            id="vulnerability-description-form-group"
            :label="this.$t('message.description')"
            label-for="vulnerability-description-input">
            <b-form-textarea id="vulnerability-description-description" v-model="vulnerability.description" rows="5" />
          </b-form-group>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border:0;padding:0">
        <template v-slot:title><i class="fa fa-newspaper-o"></i> {{ $t('message.extended') }}</template>
        <b-card>
          <b-form-group
            id="vulnerability-detail-form-group"
            :label="this.$t('message.details')"
            label-for="vulnerability-detail-input">
            <b-form-textarea id="vulnerability-detail-description" v-model="vulnerability.detail" rows="5" />
          </b-form-group>
          <b-form-group
            id="vulnerability-recommendation-form-group"
            :label="this.$t('message.recommendation')"
            label-for="vulnerability-recommendation-input">
            <b-form-textarea id="vulnerability-recommendation-description" v-model="vulnerability.recommendation" rows="5" />
          </b-form-group>
          <b-form-group
            id="vulnerability-references-form-group"
            :label="this.$t('message.references')"
            label-for="vulnerability-references-input">
            <b-form-textarea id="vulnerability-references-description" v-model="vulnerability.references" rows="5" />
          </b-form-group>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border:0;padding:0">
        <template v-slot:title><i class="fa fa-calculator"></i> {{ $t('message.cvss_v2') }}</template>
        <b-card>
          <b-row>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="severityColor(cvssV2Score.baseScore)"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV2Score.baseScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV2Score.baseScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_base_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="primaryColor"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV2Score.impactSubScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV2Score.impactSubScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_impact') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="primaryColor"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV2Score.exploitabilitySubScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV2Score.exploitabilitySubScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_exploitability') }}</h6>
            </b-col>
          </b-row>
          <b-row style="margin-top:2rem;">
            <b-col sm="6">
              <b-form-group :label="$t('message.cvss_attack_vector')" v-slot="{ cvssv2AttackVector }">
                <b-form-radio-group v-model="cvssv2Vector.av" :options="cvssv2Options.av"
                                    :aria-describedby="cvssv2AttackVector" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_access_complexity')" v-slot="{ cvssv2AccessComplexity }">
                <b-form-radio-group v-model="cvssv2Vector.ac" :options="cvssv2Options.ac"
                                    :aria-describedby="cvssv2AccessComplexity" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_authentication')" v-slot="{ cvssv2Authentication }">
                <b-form-radio-group v-model="cvssv2Vector.au" :options="cvssv2Options.au"
                                    :aria-describedby="cvssv2Authentication" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group :label="$t('message.cvss_confidentiality_impact')" v-slot="{ cvssv2ConfidentialityImpact }">
                <b-form-radio-group v-model="cvssv2Vector.c" :options="cvssv2Options.c"
                                    :aria-describedby="cvssv2ConfidentialityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_integrity_impact')" v-slot="{ cvssv2IntegrityImpact }">
                <b-form-radio-group v-model="cvssv2Vector.i" :options="cvssv2Options.i"
                                    :aria-describedby="cvssv2IntegrityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_availability_impact')" v-slot="{ cvssv2AvailabilityImpact }">
                <b-form-radio-group v-model="cvssv2Vector.a" :options="cvssv2Options.a"
                                    :aria-describedby="cvssv2AvailabilityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV2Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border:0;padding:0">
        <template v-slot:title><i class="fa fa-calculator"></i> {{ $t('message.cvss_v3') }}</template>
        <b-card>
          <b-row>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="severityColor(cvssV3Score.baseScore)"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV3Score.baseScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV3Score.baseScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_base_score_short') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="primaryColor"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV3Score.impactSubScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV3Score.impactSubScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_impact') }}</h6>
            </b-col>
            <b-col sm="4" class="text-center">
              <vue-easy-pie-chart :bar-color="primaryColor"
                                  :track-color="recessedColor"
                                  :scale-color="primaryColor"
                                  line-cap="butt"
                                  :line-width="5"
                                  :scale-length="5"
                                  :percent="cvssV3Score.exploitabilitySubScore * 10"
                                  :size="90"
                                  :animate="true"
              >{{ cvssV3Score.exploitabilitySubScore.toFixed(1) }}</vue-easy-pie-chart>
              <h6>{{ $t('message.cvss_exploitability') }}</h6>
            </b-col>
          </b-row>
          <b-row style="margin-top:2rem;">
            <b-col sm="6">
              <b-form-group :label="$t('message.cvss_attack_vector')" v-slot="{ cvssv3AttackVector }">
                <b-form-radio-group v-model="cvssv3Vector.av" :options="cvssv3Options.av"
                                    :aria-describedby="cvssv3AttackVector" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-4-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_attack_complexity')" v-slot="{ cvssv3AttackComplexity }">
                <b-form-radio-group v-model="cvssv3Vector.ac" :options="cvssv3Options.ac"
                                    :aria-describedby="cvssv3AttackComplexity" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-2-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_privileges_required')" v-slot="{ cvssv3PrivilegesRequired }">
                <b-form-radio-group v-model="cvssv3Vector.pr" :options="cvssv3Options.pr"
                                    :aria-describedby="cvssv3PrivilegesRequired" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_user_interaction')" v-slot="{ cvssv3UserInteraction }">
                <b-form-radio-group v-model="cvssv3Vector.ui" :options="cvssv3Options.ui"
                                    :aria-describedby="cvssv3UserInteraction" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-2-btn" buttons />
              </b-form-group>
            </b-col>
            <b-col sm="6">
              <b-form-group :label="$t('message.cvss_scope')" v-slot="{ cvssv3Scope }">
                <b-form-radio-group v-model="cvssv3Vector.s" :options="cvssv3Options.s"
                                    :aria-describedby="cvssv3Scope" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-2-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_confidentiality_impact')" v-slot="{ cvssv3ConfidentialityImpact }">
                <b-form-radio-group v-model="cvssv3Vector.c" :options="cvssv3Options.c"
                                    :aria-describedby="cvssv3ConfidentialityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_integrity_impact')" v-slot="{ cvssv3IntegrityImpact }">
                <b-form-radio-group v-model="cvssv3Vector.i" :options="cvssv3Options.i"
                                    :aria-describedby="cvssv3IntegrityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
              <b-form-group :label="$t('message.cvss_availability_impact')" v-slot="{ cvssv3AvailabilityImpact }">
                <b-form-radio-group v-model="cvssv3Vector.a" :options="cvssv3Options.a"
                                    :aria-describedby="cvssv3AvailabilityImpact" name="radios-btn-default"
                                    v-on:change="generateCvssV3Vector" button-variant="outline-primary"
                                    class="cvss-calc cvss-calc-3-btn" buttons />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border:0;padding:0">
        <template v-slot:title><i class="fa fa-cubes"></i> {{ $t('message.affected_components') }}</template>
        <b-card>
          <b-form-group>
            <div class="list-group" style="height:20rem; max-height:20rem; overflow:scroll; -webkit-overflow-scrolling:touch;">
            <span v-for="affectedComponent in vulnerability.affectedComponents">
              <actionable-list-group-item :value="affectedComponentLabel(affectedComponent)" :delete-icon="true" v-on:actionClicked="removeAffectedComponent(affectedComponent)"/>
            </span>
              <actionable-list-group-item :add-icon="true" v-on:actionClicked="$root.$emit('bv::show::modal', 'addAffectedComponentModal')"/>
            </div>
          </b-form-group>
        </b-card>
      </b-tab>
      <b-tab class="body-bg-color" style="border:0;padding:0">
        <template v-slot:title><i class="fa fa-calendar"></i> {{ $t('message.dates') }}</template>
        <b-card>

          <b-input-group-form-datepicker id="vulnerability-created-input" input-group-size="mb-3" v-model="vulnerability.created"
                                         lazy="true" required="false" feedback="false" autofocus="false" placeholder="YYYY-MM-DD"
                                         :label="$t('message.created')" :tooltip="this.$t('message.vulnerability_created_desc')"/>
          <b-input-group-form-datepicker id="vulnerability-published_input" input-group-size="mb-3" v-model="vulnerability.published"
                                         lazy="true" required="false" feedback="false" autofocus="false" placeholder="YYYY-MM-DD"
                                         :label="$t('message.published')" :tooltip="this.$t('message.vulnerability_published_desc')"/>
          <b-input-group-form-datepicker id="vulnerability-updated-input" input-group-size="mb-3" v-model="vulnerability.updated"
                                         lazy="true" required="false" feedback="false" autofocus="false" placeholder="YYYY-MM-DD"
                                         :label="$t('message.updated')" :tooltip="this.$t('message.vulnerability_updated_desc')"/>
        </b-card>
      </b-tab>
    </b-tabs>
    <template v-slot:modal-footer="{ cancel }">
      <b-button size="md" variant="secondary" @click="cancel()">{{ $t('message.close') }}</b-button>
      <b-button size="md" variant="primary" @click="createVulnerability()">{{ $t('message.create') }}</b-button>
    </template>
    <select-cwe-modal v-on:selection="updateCweSelection" />
    <add-affected-component-modal v-on:affectedComponentAdded="addAffectedComponent"/>
  </b-modal>
</template>

<script>
import BInputGroupFormDatepicker from "../../../forms/BInputGroupFormDatepicker";
import BInputGroupFormInput from "../../../forms/BInputGroupFormInput";
import BInputGroupFormSelect from "../../../forms/BInputGroupFormSelect";
import VueTagsInput from '@johmun/vue-tags-input';
import { Switch as cSwitch } from '@coreui/vue';
import permissionsMixin from "../../../mixins/permissionsMixin";
import ActionableListGroupItem from "../../components/ActionableListGroupItem";
import SelectCweModal from "./SelectCweModal";
import AddAffectedComponentModal from "./AddAffectedComponentModal";
import VueEasyPieChart from 'vue-easy-pie-chart'
import {getStyle} from "@coreui/coreui/dist/js/coreui-utilities";
import { isEqual } from 'lodash-es';
import xssFilters from "xss-filters";
import common from "../../../shared/common";

export default {
  name: "VulnerabilityCreateVulnerabilityModal",
  mixins: [permissionsMixin],
  components: {
    ActionableListGroupItem,
    BInputGroupFormDatepicker,
    BInputGroupFormInput,
    BInputGroupFormSelect,
    SelectCweModal,
    AddAffectedComponentModal,
    VueEasyPieChart,
    VueTagsInput,
    cSwitch
  },
  data() {
    return {
      primaryColor: getStyle('--primary'),
      recessedColor: getStyle('--recessed'),
      criticalColor: getStyle('--severity-critical'),
      highColor: getStyle('--severity-high'),
      mediumColor: getStyle('--severity-medium'),
      lowColor: getStyle('--severity-low'),
      currentCritical: 7.1,
      cvssv2Test: null,
      selectableCwes: [],
      vulnerability: {
        vulnId: null, // This has to be null on initial load so that the vulnId gets populated.
        source: 'INTERNAL',
        affectedComponents: [],
      },
      severities: [
        { value: null, text: this.$i18n.t('severity.derive_from_cvss') },
        { value: 'CRITICAL', text: this.$i18n.t('severity.critical') },
        { value: 'HIGH', text: this.$i18n.t('severity.high') },
        { value: 'MEDIUM', text: this.$i18n.t('severity.medium') },
        { value: 'LOW', text: this.$i18n.t('severity.low') },
        { value: 'INFO', text: this.$i18n.t('severity.info') },
        { value: 'UNASSIGNED', text: this.$i18n.t('severity.unassigned') }
      ],
      cvssv2Vector: {
        av: null,
        ac: null,
        au: null,
        c: null,
        i: null,
        a: null
      },
      cvssv3Vector: {
        av: null,
        ac: null,
        pr: null,
        ui: null,
        s: null,
        c: null,
        i: null,
        a: null
      },
      cvssV2Score: {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0
      },
      cvssV3Score: {
        baseScore: 0,
        impactSubScore: 0,
        exploitabilitySubScore: 0
      },
      cvssv2Options: {
        av: [
          {text: this.$i18n.t('message.cvss_local'), value: 'L'},
          {text: this.$i18n.t('message.cvss_adjacent'), value: 'A'},
          {text: this.$i18n.t('message.cvss_network'), value: 'N'}
        ],
        ac: [
          {text: this.$i18n.t('message.cvss_high'), value: 'H'},
          {text: this.$i18n.t('message.cvss_medium'), value: 'M'},
          {text: this.$i18n.t('message.cvss_low'), value: 'L'}
        ],
        au: [
          {text: this.$i18n.t('message.cvss_multiple'), value: 'M'},
          {text: this.$i18n.t('message.cvss_single'), value: 'S'},
          {text: this.$i18n.t('message.cvss_none'), value: 'N'}
        ],
        c: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_partial'), value: 'P'},
          {text: this.$i18n.t('message.cvss_complete'), value: 'C'}
        ],
        i: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_partial'), value: 'P'},
          {text: this.$i18n.t('message.cvss_complete'), value: 'C'}
        ],
        a: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_partial'), value: 'P'},
          {text: this.$i18n.t('message.cvss_complete'), value: 'C'}
        ]
      },
      cvssv3Options: {
        av: [
          {text: this.$i18n.t('message.cvss_physical'), value: 'P'},
          {text: this.$i18n.t('message.cvss_local'), value: 'L'},
          {text: this.$i18n.t('message.cvss_adjacent'), value: 'A'},
          {text: this.$i18n.t('message.cvss_network'), value: 'N'}
        ],
        ac: [
          {text: this.$i18n.t('message.cvss_low'), value: 'L'},
          {text: this.$i18n.t('message.cvss_high'), value: 'H'}
        ],
        pr: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_low'), value: 'L'},
          {text: this.$i18n.t('message.cvss_high'), value: 'H'}
        ],
        ui: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_required'), value: 'R'}
        ],
        s: [
          {text: this.$i18n.t('message.cvss_unchanged'), value: 'U'},
          {text: this.$i18n.t('message.cvss_changed'), value: 'C'}
        ],
        c: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_low'), value: 'L'},
          {text: this.$i18n.t('message.cvss_high'), value: 'H'}
        ],
        i: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_low'), value: 'L'},
          {text: this.$i18n.t('message.cvss_high'), value: 'H'}
        ],
        a: [
          {text: this.$i18n.t('message.cvss_none'), value: 'N'},
          {text: this.$i18n.t('message.cvss_low'), value: 'L'},
          {text: this.$i18n.t('message.cvss_high'), value: 'H'}
        ]
      }
    }
  },
  computed: {
    computedCvssSeverity() {
      let severity = "UNASSIGNED";
      if (this.cvssV2Score && this.cvssV2Score.baseScore) {
        if (this.cvssV2Score.baseScore >= 7) {
          severity = "HIGH";
        } else if (this.cvssV2Score.baseScore >= 4) {
          severity = "MEDIUM";
        } else if (this.cvssV2Score.baseScore > 0) {
          severity = "LOW";
        }
      }
      if (this.cvssV3Score && this.cvssV3Score.baseScore) {
        if (this.cvssV3Score.baseScore >= 9) {
          severity = "CRITICAL";
        } else if (this.cvssV3Score.baseScore >= 7) {
          severity = "HIGH";
        } else if (this.cvssV3Score.baseScore >= 4) {
          severity = "MEDIUM";
        } else if (this.cvssV3Score.baseScore > 0) {
          severity = "LOW";
        }
      }
      return severity;
    }
  },
  methods: {
    onShow: function() {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}/vulnId`;
      this.axios.get(url).then((response) => {
        this.vulnerability.vulnId = response.data
      });
    },
    createVulnerability: function() {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_VULNERABILITY}`;
      this.axios.put(url, {
        vulnId: this.vulnerability.vulnId,
        source: 'INTERNAL',
        title: this.vulnerability.title,
        description: this.vulnerability.description,
        detail: this.vulnerability.detail,
        recommendation: this.vulnerability.recommendation,
        references: this.vulnerability.references,
        created: this.vulnerability.created,
        published: this.vulnerability.published,
        updated: this.vulnerability.updated,
        severity: (this.vulnerability.severity != null) ? this.vulnerability.severity : this.computedCvssSeverity,
        cvssV2Vector: this.generateCvssV2Vector(),
        cvssV3Vector: this.generateCvssV3Vector(),
        cwes: this.selectableCwes,
        affectedComponents: this.vulnerability.affectedComponents
      }).then((response) => {
        this.$emit('refreshTable');
        this.$toastr.s(this.$t('message.vulnerability_created'));
        this.$router.replace({ path: "/vulnerabilities/INTERNAL/" + this.vulnerability.vulnId });
      }).catch((error) => {
        this.$toastr.w(this.$t('condition.unsuccessful_action'));
      }).finally(() => {
        this.$root.$emit('bv::hide::modal', 'vulnerabilityCreateVulnerabilityModal');
      });
    },
    updateCweSelection: function (selections) {
      this.$root.$emit('bv::hide::modal', 'selectCweModal');
      for (let i=0; i<selections.length; i++) {
        let selection = selections[i];
        this.selectableCwes.push({"cweId": selection.cweId, "name": selection.name});
      }
    },
    removeCwe: function(cwe) {
      for (let i=0; i<this.selectableCwes.length; i++) {
        let selectedCwe = this.selectableCwes[i];
        if (selectedCwe.cweId === cwe.cweId) {
          this.selectableCwes.splice(i, 1);
          i--;
        }
      }
    },
    removeAffectedComponent: function(affectedComponent) {
      for (let i=0; i<this.vulnerability.affectedComponents.length; i++) {
        let a = this.vulnerability.affectedComponents[i];
        if (isEqual(a, affectedComponent)) {
          this.vulnerability.affectedComponents.splice(i, 1);
        }
      }
    },
    generateCvssV2Vector: function() {
      if (this.cvssv2Vector.av != null && this.cvssv2Vector.ac != null && this.cvssv2Vector.au != null
        && this.cvssv2Vector.c != null && this.cvssv2Vector.i != null && this.cvssv2Vector.a != null) {
        let vector = "(AV:"+this.cvssv2Vector.av+"/AC:"+this.cvssv2Vector.ac+"/Au:"+this.cvssv2Vector.au+"/C:"+this.cvssv2Vector.c+"/I:"+this.cvssv2Vector.i+"/A:"+this.cvssv2Vector.a+")";
        this.retrieveCvssScore(vector, 2);
        return vector;
      }
      return null;
    },
    generateCvssV3Vector: function() {
      if (this.cvssv3Vector.av != null && this.cvssv3Vector.ac != null && this.cvssv3Vector.pr != null
        && this.cvssv3Vector.ui != null && this.cvssv3Vector.s != null
        && this.cvssv3Vector.c != null && this.cvssv3Vector.i != null && this.cvssv3Vector.a != null) {
        let vector = "(AV:"+this.cvssv3Vector.av+"/AC:"+this.cvssv3Vector.ac+"/PR:"+this.cvssv3Vector.pr+"/UI:"+this.cvssv3Vector.ui+"/S:"+this.cvssv3Vector.s+ "/C:"+this.cvssv3Vector.c+"/I:"+this.cvssv3Vector.i+"/A:"+this.cvssv3Vector.a+")";
        this.retrieveCvssScore(vector, 3);
        return vector;
      }
      return null;
    },
    retrieveCvssScore: function(vector, cvssVersion) {
      let url = `${this.$api.BASE_URL}/${this.$api.URL_CALCULATOR_CVSS}`;
      this.axios.get(url, {params: {vector: vector}}).then((response) => {
        if (cvssVersion === 2) {
          this.cvssV2Score.baseScore = response.data.baseScore
          this.cvssV2Score.impactSubScore = response.data.impactSubScore
          this.cvssV2Score.exploitabilitySubScore = response.data.exploitabilitySubScore
        } else if (cvssVersion === 3) {
          this.cvssV3Score.baseScore = response.data.baseScore
          this.cvssV3Score.impactSubScore = response.data.impactSubScore
          this.cvssV3Score.exploitabilitySubScore = response.data.exploitabilitySubScore
        }
      }).catch((error) => {
        this.$toastr.w(this.$t('condition.unsuccessful_action'));
      });
    },
    severityColor(score) {
      if (!score) {
        return this.primaryColor;
      } else if (score >= 9.0){
        return this.criticalColor;
      } else if (score >= 7.0) {
        return this.highColor;
      } else if (score >= 4.0) {
        return this.mediumColor;
      } else {
        return this.lowColor;
      }
    },
    addAffectedComponent: function(affectedComponent) {
      if (!this.vulnerability.affectedComponents) {
        this.vulnerability.affectedComponents = [];
      }
      this.vulnerability.affectedComponents.push(affectedComponent);
    },
    affectedComponentLabel: function(affectedComponent) {
      let s = affectedComponent.identity;
      if (affectedComponent.versionType === 'RANGE') {
        s += " ( ";
        if (affectedComponent.versionStartExcluding) {
          s += ">" + affectedComponent.versionStartExcluding;
        } else if (affectedComponent.versionStartIncluding) {
          s += ">=" + affectedComponent.versionStartIncluding;
        }
        if (affectedComponent.versionEndExcluding) {
          s += "|<" + affectedComponent.versionEndExcluding;
        } else if (affectedComponent.versionEndIncluding) {
          s += "|<=" + affectedComponent.versionEndIncluding;
        }
        s += " )";
      }
      return s;
    },
    resetValues: function () {
      this.cvssV2Score.baseScore = 0;
      this.cvssV2Score.impactSubScore = 0;
      this.cvssV2Score.exploitabilitySubScore = 0
      this.vulnerability = {
        vulnId: null // This has to be null on initial load so that the vulnId gets populated.
      };
    }
  }
}
</script>

<style lang="scss">
@import "../../../assets/scss/vendors/vue-tags-input/vue-tags-input";
</style>

<style scoped>
.tab-content .tab-pane{
  padding: 0 !important;
}
.tab-content {
  border: 0 !important;
}
.card {
  border:0;
  padding:0;
  margin-bottom:0;
}
</style>
<style>
.cvss-calc {
  width: 100%;
}
.cvss-calc-2-btn > .btn {
  width: 50%;
  max-width: 50%;
}
.cvss-calc-3-btn > .btn {
  width: 33%;
  max-width: 33%;
}
.cvss-calc-4-btn > .btn {
  width: 25%;
  max-width: 25%;
}
.vue-easy-pie-chart .inner-text {
  color: var(--primary-lighter);
  font-size: 2rem;
  font-weight: bold;
}
</style>
